import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { Theme } from "@mui/material/styles";
import { AnyData, ImobiliarioEdificacao, ProjetosCamadasFormulariosCampo } from "../../../../interfaces";
import { isCurrentColorModeDark } from "../../../../providers/Theme";
import {
  EdificacaoSubunidadeNode,
  EdificacaoUnidadePrincipalNode,
  EdificacaoVinculoNode,
  EdificacoesHierarchy,
  isVinculoEdificacaoAtivo,
} from "../../../../utils/edificacoesHierarchy";

/** Mesmo verde das abas horizontais da Section. */
const TAB_ACCENT_GREEN = "#009c66";

const CAMPOS_RESUMO_OCULTOS = [/utiliza[cç][aã]o\s*do\s*im[oó]vel/i];

function getEdificacoesPalette(theme: Theme) {
  const isDark = isCurrentColorModeDark();
  const green = theme.palette.secondary.main || TAB_ACCENT_GREEN;
  const greenLight = theme.palette.secondary.light || green;

  const subunidade = isDark ? "#8ecae6" : "#1976d2";
  const complementar = isDark ? "#f4d58d" : "#ed6c02";

  const headerText = isDark ? "#ffffff" : theme.palette.text.primary;

  return {
    green,
    headerText,
    accentSubunidade: subunidade,
    accentComplementar: complementar,
    linkedBg: alpha(greenLight, 0.1),
    selectedBg: alpha(green, isDark ? 0.18 : 0.1),
    hoverBg: alpha(greenLight, 0.14),
    panelBg: isDark ? theme.palette.background.paper : alpha(green, 0.04),
    nestedBg: isDark ? theme.palette.background.default : alpha(theme.palette.grey[500], 0.08),
    borderSubtle: alpha(green, isDark ? 0.35 : 0.25),
    borderSubunidade: alpha(subunidade, isDark ? 0.55 : 0.4),
    borderComplementar: alpha(complementar, isDark ? 0.55 : 0.45),
    borderStrong: green,
    scrollTrack: isDark ? theme.palette.background.default : theme.palette.grey[100],
  };
}

function getScrollAreaStyles(theme: Theme) {
  const palette = getEdificacoesPalette(theme);
  const isDark = isCurrentColorModeDark();
  const thumb = isDark ? alpha("#ffffff", 0.35) : alpha(theme.palette.grey[600], 0.4);
  const thumbHover = isDark ? alpha("#ffffff", 0.55) : alpha(theme.palette.grey[600], 0.6);

  return {
    scrollbarWidth: "thin",
    scrollbarColor: `${thumb} ${palette.scrollTrack}`,
    "&::-webkit-scrollbar": {
      width: 8,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: palette.scrollTrack,
      borderRadius: 8,
      marginBlock: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: thumb,
      borderRadius: 8,
      border: `2px solid ${palette.scrollTrack}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
  };
}

type ItemAccent = "principal" | "subunidade" | "complementar" | "neutral";

function getItemAccentStyles(theme: Theme, accent: ItemAccent) {
  const palette = getEdificacoesPalette(theme);
  const isDark = isCurrentColorModeDark();

  if (accent === "principal") {
    return {
      borderSubtle: palette.borderSubtle,
      borderStrong: palette.borderStrong,
      linkedBg: palette.linkedBg,
      selectedBg: palette.selectedBg,
      hoverBg: palette.hoverBg,
      hoverBorder: palette.borderSubtle,
    };
  }

  if (accent === "subunidade") {
    const color = palette.accentSubunidade;
    return {
      borderSubtle: alpha(color, isDark ? 0.35 : 0.3),
      borderStrong: color,
      linkedBg: alpha(color, 0.12),
      selectedBg: alpha(color, isDark ? 0.2 : 0.12),
      hoverBg: alpha(color, 0.16),
      hoverBorder: alpha(color, isDark ? 0.5 : 0.4),
    };
  }

  if (accent === "complementar") {
    const color = palette.accentComplementar;
    return {
      borderSubtle: alpha(color, isDark ? 0.35 : 0.3),
      borderStrong: color,
      linkedBg: alpha(color, 0.12),
      selectedBg: alpha(color, isDark ? 0.2 : 0.12),
      hoverBg: alpha(color, 0.16),
      hoverBorder: alpha(color, isDark ? 0.5 : 0.4),
    };
  }

  return {
    borderSubtle: theme.palette.divider,
    borderStrong: theme.palette.text.secondary,
    linkedBg: isDark ? alpha("#ffffff", 0.05) : alpha("#000000", 0.04),
    selectedBg: isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.06),
    hoverBg: isDark ? alpha("#ffffff", 0.07) : alpha("#000000", 0.05),
    hoverBorder: theme.palette.divider,
  };
}

interface EdificacoesHierarquicasTabsProps {
  hierarchy: EdificacoesHierarchy;
  selectedId: string | undefined;
  selectedVinculoId?: string;
  onSelect: (edificacaoId: string, vinculoId?: string) => void;
  resumo?: ProjetosCamadasFormulariosCampo[];
  isLinked: (edificacaoId: string | number) => boolean;
  showDisabledBadge?: boolean;
  footer?: React.ReactNode;
}

function getVinculoSubtitle(vinculo: ImobiliarioEdificacao): string | undefined {
  const partes: string[] = [];
  if (vinculo.codigo_construcao) partes.push(`Cód. ${vinculo.codigo_construcao}`);
  if (vinculo.inscricao_subunidade) partes.push(String(vinculo.inscricao_subunidade));
  else if (vinculo.inscricao_unidade) partes.push(String(vinculo.inscricao_unidade));
  if (vinculo.numero_cadastro != null) partes.push(`Cad. ${vinculo.numero_cadastro}`);
  return partes.length > 0 ? partes.join(" · ") : undefined;
}

function shouldShowResumoCampo(campo: ProjetosCamadasFormulariosCampo): boolean {
  const nome = String(campo.nome ?? "");
  const coluna = String(campo.tblNomeColuna ?? "");
  return !CAMPOS_RESUMO_OCULTOS.some((pattern) => pattern.test(nome) || pattern.test(coluna));
}

function CollapseToggle(props: { expanded: boolean; color: string; onToggle: () => void }) {
  return (
    <IconButton
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        props.onToggle();
      }}
      sx={{ p: 0.25, color: props.color }}
      aria-label={props.expanded ? "Recolher" : "Expandir"}
    >
      {props.expanded ? (
        <ExpandMoreIcon sx={{ fontSize: 20 }} />
      ) : (
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      )}
    </IconButton>
  );
}

function CollapsibleSection(props: {
  label: string;
  icon?: React.ReactNode;
  color: string;
  borderColor: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  sx?: object;
}) {
  const [expanded, setExpanded] = useState(props.defaultExpanded ?? true);

  return (
    <Box sx={{ mt: 1, ...props.sx }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={() => setExpanded((value) => !value)}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          px: 0.5,
          mb: expanded ? 0.5 : 0,
        }}
      >
        <CollapseToggle expanded={expanded} color={props.color} onToggle={() => setExpanded((v) => !v)} />
        {props.icon}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: props.color,
          }}
        >
          {props.label}
        </Typography>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <NestedGroup borderColor={props.borderColor}>{props.children}</NestedGroup>
      </Collapse>
    </Box>
  );
}

function NestedGroup(props: { children: React.ReactNode; borderColor: string; sx?: object }) {
  return (
    <Box
      sx={{
        ml: 1.5,
        pl: 1.5,
        borderLeft: 2,
        borderColor: props.borderColor,
        borderRadius: "0 8px 8px 0",
        bgcolor: (theme) => getEdificacoesPalette(theme).nestedBg,
        py: 0.5,
        ...props.sx,
      }}
    >
      {props.children}
    </Box>
  );
}

function renderResumo(item: AnyData, resumo?: ProjetosCamadasFormulariosCampo[]) {
  const camposVisiveis = resumo?.filter(shouldShowResumoCampo);
  if (!camposVisiveis?.length) return null;

  return (
    <Stack spacing={0.25} sx={{ mt: 0.5, minWidth: 0 }}>
      {camposVisiveis.map((campo, index) => {
        const valor = item[campo.tblNomeColuna];
        if (valor === null || valor === undefined || valor === "") return null;
        return (
          <Tooltip key={index} title={`${campo.nome}: ${valor}`}>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ display: "block", maxWidth: "100%", fontSize: "0.75rem" }}
            >
              <Box component="span" sx={{ fontWeight: 500, color: "text.primary", mr: 0.5 }}>
                {campo.nome}:
              </Box>
              {valor}
            </Typography>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function EdificacaoTabItem(props: {
  node: EdificacaoVinculoNode;
  selectedId: string | undefined;
  selectedVinculoId?: string;
  onSelect: (edificacaoId: string, vinculoId?: string) => void;
  resumo?: ProjetosCamadasFormulariosCampo[];
  isLinked: (edificacaoId: string | number) => boolean;
  showDisabledBadge?: boolean;
  subtitle?: string;
  accent?: ItemAccent;
}) {
  const theme = useTheme();
  const edificacao = props.node.edificacao;
  if (!edificacao) return null;

  const accent = props.accent ?? "neutral";
  const itemStyles = getItemAccentStyles(theme, accent);
  const edificacaoId = String(edificacao.id);
  const vinculoId = props.node.vinculo.id ? String(props.node.vinculo.id) : undefined;
  const isSelected = vinculoId
    ? props.selectedVinculoId === vinculoId
    : props.selectedId === edificacaoId;
  const isHighlighted =
    isVinculoEdificacaoAtivo(props.node.vinculo) && props.isLinked(edificacao.id);
  const subtitle = props.subtitle ?? getVinculoSubtitle(props.node.vinculo);
  const isDisabled = props.node.vinculo.status === 2 || props.node.vinculo.status === "2";

  return (
    <Box
      onClick={() => props.onSelect(edificacaoId, vinculoId)}
      sx={{
        cursor: "pointer",
        borderRadius: 1.5,
        px: 1.25,
        py: 1,
        mb: 0.75,
        border: 1,
        borderColor: isSelected ? itemStyles.borderStrong : "divider",
        borderLeftWidth: 3,
        borderLeftColor: isSelected ? itemStyles.borderStrong : itemStyles.borderSubtle,
        backgroundColor: isSelected
          ? itemStyles.selectedBg
          : isHighlighted
            ? itemStyles.linkedBg
            : "background.paper",
        boxShadow: "none",
        transition: theme.transitions.create(["background-color", "border-color"], {
          duration: 150,
        }),
        "&:hover": {
          backgroundColor: itemStyles.hoverBg,
          borderColor: itemStyles.hoverBorder,
        },
      }}
    >
      <Tooltip title={`ID da edificação: ${edificacao.id}`}>
        <Typography variant="body2" fontWeight={isSelected ? 700 : 500} sx={{ fontSize: "0.85rem" }}>
          Edificação #{edificacao.id}
          {props.showDisabledBadge && isDisabled && (
            <Box
              component="span"
              sx={{
                ml: 0.75,
                px: 0.65,
                py: 0.15,
                borderRadius: 0.75,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                color: "warning.dark",
                fontSize: "0.68rem",
                fontWeight: 700,
                lineHeight: 1.4,
                verticalAlign: "middle",
              }}
            >
              Desativado
            </Box>
          )}
        </Typography>
      </Tooltip>

      {subtitle && (
        <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.25, fontSize: "0.75rem" }}>
          {subtitle}
        </Typography>
      )}

      {renderResumo(edificacao, props.resumo)}
    </Box>
  );
}

function SubunidadeGroup(props: {
  subunidade: EdificacaoSubunidadeNode;
  selectedId: string | undefined;
  selectedVinculoId?: string;
  onSelect: (edificacaoId: string, vinculoId?: string) => void;
  resumo?: ProjetosCamadasFormulariosCampo[];
  isLinked: (edificacaoId: string | number) => boolean;
  showDisabledBadge?: boolean;
  headerText: string;
  borderComplementar: string;
}) {
  return (
    <Box sx={{ mb: 0.5 }}>
      <EdificacaoTabItem
        node={props.subunidade}
        selectedId={props.selectedId}
        selectedVinculoId={props.selectedVinculoId}
        onSelect={props.onSelect}
        resumo={props.resumo}
        isLinked={props.isLinked}
        showDisabledBadge={props.showDisabledBadge}
        accent="subunidade"
        subtitle={
          props.subunidade.inscricaoSubunidade
            ? `Inscrição ${props.subunidade.inscricaoSubunidade}`
            : undefined
        }
      />

      {props.subunidade.complementares.length > 0 && (
        <CollapsibleSection
          label="Complementares da subunidade"
          color={props.headerText}
          borderColor={props.borderComplementar}
          icon={<LayersOutlinedIcon sx={{ fontSize: 15, color: props.headerText }} />}
        >
          {props.subunidade.complementares.map((complementar) => (
            <EdificacaoTabItem
              key={String(complementar.vinculo.id)}
              node={complementar}
              selectedId={props.selectedId}
              selectedVinculoId={props.selectedVinculoId}
              onSelect={props.onSelect}
              resumo={props.resumo}
              isLinked={props.isLinked}
              showDisabledBadge={props.showDisabledBadge}
              accent="complementar"
            />
          ))}
        </CollapsibleSection>
      )}
    </Box>
  );
}

function UnidadePrincipalGroup(props: {
  unidade: EdificacaoUnidadePrincipalNode;
  selectedId: string | undefined;
  selectedVinculoId?: string;
  onSelect: (edificacaoId: string, vinculoId?: string) => void;
  resumo?: ProjetosCamadasFormulariosCampo[];
  isLinked: (edificacaoId: string | number) => boolean;
  showDisabledBadge?: boolean;
}) {
  const theme = useTheme();
  const palette = getEdificacoesPalette(theme);
  const [expanded, setExpanded] = useState(true);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: palette.panelBg,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={() => setExpanded((value) => !value)}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          mb: expanded ? 1 : 0,
        }}
      >
        <CollapseToggle
          expanded={expanded}
          color={palette.headerText}
          onToggle={() => setExpanded((value) => !value)}
        />
        <ApartmentOutlinedIcon sx={{ fontSize: 20, color: palette.headerText }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.headerText, fontSize: "0.875rem" }}>
            Unidade principal
          </Typography>
          {props.unidade.inscricaoUnidade && (
            <Typography
              variant="body2"
              noWrap
              display="block"
              sx={{ color: palette.headerText, opacity: 0.85, fontSize: "0.8rem" }}
            >
              {props.unidade.inscricaoUnidade}
            </Typography>
          )}
        </Box>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <EdificacaoTabItem
          node={props.unidade}
          selectedId={props.selectedId}
          selectedVinculoId={props.selectedVinculoId}
          onSelect={props.onSelect}
          resumo={props.resumo}
          isLinked={props.isLinked}
          showDisabledBadge={props.showDisabledBadge}
          accent="principal"
        />

        {props.unidade.subunidades.length > 0 && (
          <CollapsibleSection
            label="Subunidades"
            color={palette.headerText}
            borderColor={palette.borderSubunidade}
            icon={<AccountTreeOutlinedIcon sx={{ fontSize: 15, color: palette.headerText }} />}
          >
            {props.unidade.subunidades.map((subunidade) => (
              <SubunidadeGroup
                key={String(subunidade.vinculo.id)}
                subunidade={subunidade}
                selectedId={props.selectedId}
                selectedVinculoId={props.selectedVinculoId}
                onSelect={props.onSelect}
                resumo={props.resumo}
                isLinked={props.isLinked}
                showDisabledBadge={props.showDisabledBadge}
                headerText={palette.headerText}
                borderComplementar={palette.borderComplementar}
              />
            ))}
          </CollapsibleSection>
        )}

        {props.unidade.complementares.length > 0 && (
          <CollapsibleSection
            label="Complementares da unidade"
            color={palette.headerText}
            borderColor={palette.borderComplementar}
            icon={<LayersOutlinedIcon sx={{ fontSize: 15, color: palette.headerText }} />}
          >
            {props.unidade.complementares.map((complementar) => (
              <EdificacaoTabItem
                key={String(complementar.vinculo.id)}
                node={complementar}
                selectedId={props.selectedId}
                selectedVinculoId={props.selectedVinculoId}
                onSelect={props.onSelect}
                resumo={props.resumo}
                isLinked={props.isLinked}
                showDisabledBadge={props.showDisabledBadge}
                accent="complementar"
              />
            ))}
          </CollapsibleSection>
        )}
      </Collapse>
    </Box>
  );
}

function GroupPanel(props: {
  title: string;
  children: React.ReactNode;
  muted?: boolean;
  headerColor: string;
  defaultExpanded?: boolean;
}) {
  const theme = useTheme();
  const palette = getEdificacoesPalette(theme);
  const [expanded, setExpanded] = useState(props.defaultExpanded ?? true);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.25,
        borderRadius: 2,
        border: 1,
        borderStyle: props.muted ? "dashed" : "solid",
        borderColor: "divider",
        bgcolor: palette.nestedBg,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={() => setExpanded((value) => !value)}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          mb: expanded ? 1 : 0,
        }}
      >
        <CollapseToggle
          expanded={expanded}
          color={props.headerColor}
          onToggle={() => setExpanded((value) => !value)}
        />
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: props.headerColor, fontSize: "0.875rem" }}>
          {props.title}
        </Typography>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        {props.children}
      </Collapse>
    </Box>
  );
}

export default function EdificacoesHierarquicasTabs(props: EdificacoesHierarquicasTabsProps) {
  const theme = useTheme();
  const palette = getEdificacoesPalette(theme);
  const isDark = isCurrentColorModeDark();
  const {
    hierarchy,
    selectedId,
    selectedVinculoId,
    onSelect,
    resumo,
    isLinked,
    showDisabledBadge,
    footer,
  } = props;
  const hasContent =
    hierarchy.unidades.length > 0 ||
    hierarchy.orphans.length > 0 ||
    hierarchy.semVinculos.length > 0;

  if (!hasContent) {
    return (
      <Box
        sx={{
          py: 3,
          px: 2,
          textAlign: "center",
          borderRadius: 2,
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
        }}
      >
        <ApartmentOutlinedIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
          Nenhuma edificação vinculada a este imobiliário.
        </Typography>
      </Box>
    );
  }

  const listContent = (
    <>
      {hierarchy.unidades.map((unidade) => (
        <UnidadePrincipalGroup
          key={String(unidade.vinculo.id)}
          unidade={unidade}
          selectedId={selectedId}
          selectedVinculoId={selectedVinculoId}
          onSelect={onSelect}
          resumo={resumo}
          isLinked={isLinked}
          showDisabledBadge={showDisabledBadge}
        />
      ))}

      {hierarchy.orphans.length > 0 && (
        <GroupPanel title="Outros vínculos" muted headerColor={palette.headerText}>
          {hierarchy.orphans.map((node) => (
            <EdificacaoTabItem
              key={String(node.vinculo.id)}
              node={node}
              selectedId={selectedId}
              selectedVinculoId={selectedVinculoId}
              onSelect={onSelect}
              resumo={resumo}
              isLinked={isLinked}
              showDisabledBadge={showDisabledBadge}
              accent="neutral"
            />
          ))}
        </GroupPanel>
      )}

      {hierarchy.semVinculos.length > 0 && (
        <GroupPanel title="Sem vínculos" muted headerColor={palette.headerText} defaultExpanded>
          {hierarchy.semVinculos.map((node) => (
            <EdificacaoTabItem
              key={String(node.vinculo.id)}
              node={node}
              selectedId={selectedId}
              onSelect={onSelect}
              resumo={resumo}
              isLinked={isLinked}
              showDisabledBadge={false}
              accent="neutral"
            />
          ))}
        </GroupPanel>
      )}
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: isDark ? palette.scrollTrack : "background.paper",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          maxHeight: "min(42vh, 420px)",
          minHeight: 96,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.5,
          py: 1.25,
          pr: 1,
          boxSizing: "border-box",
          ...getScrollAreaStyles(theme),
        }}
      >
        {listContent}
      </Box>

      {footer && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: isDark ? theme.palette.background.paper : theme.palette.background.default,
            py: 0.25,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}
